import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ENDPOINTS from "../../config/apiUrls";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "../../config/dataApi";
import { toast } from "react-toastify";

// Async thunks
export const fetchApps = createAsyncThunk(
  "apps/fetchApps",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getRequest(ENDPOINTS.APPS.LIST);
      return response.data || [];
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch apps"
      );
    }
  },
  {
    condition: (force = false, { getState }) => {
      if (!force && getState().apps.loading) {
        return false; // block if already loading and not forced
      }
      return true;
    },
  }
);

export const createApp = createAsyncThunk(
  "apps/createApp",
  async (appData, { rejectWithValue }) => {
    try {
      const response = await postRequest(ENDPOINTS.APPS.CREATE, appData);
      toast.success("App created successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create app");
      return rejectWithValue(
        error.response?.data?.message || "Failed to create app"
      );
    }
  }
);

export const updateApp = createAsyncThunk(
  "apps/updateApp",
  async ({ id, appData }, { rejectWithValue }) => {
    try {
      const response = await putRequest(ENDPOINTS.APPS.UPDATE(id), appData);
      toast.success("App updated successfully");
      return { id, data: response.data };
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update app");
      return rejectWithValue(
        error.response?.data?.message || "Failed to update app"
      );
    }
  }
);

export const deleteApp = createAsyncThunk(
  "apps/deleteApp",
  async (id, { rejectWithValue }) => {
    try {
      await deleteRequest(ENDPOINTS.APPS.DELETE(id));
      toast.success("App deleted successfully");
      return id;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete app");
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete app"
      );
    }
  }
);

const initialState = {
  apps: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
};

const appsSlice = createSlice({
  name: "apps",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch apps
      .addCase(fetchApps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.loading = false;
        state.apps = action.payload;
      })
      .addCase(fetchApps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create app
      .addCase(createApp.pending, (state) => {
        state.loading = true;
      })
      .addCase(createApp.fulfilled, (state, action) => {
        state.loading = false;
        state.apps.push(action.payload); // optional since fetchApps runs again
      })
      .addCase(createApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update app
      .addCase(updateApp.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateApp.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.apps.findIndex(
          (app) => app.id === action.payload.id
        );
        if (index !== -1) {
          state.apps[index] = {
            ...state.apps[index],
            ...action.payload.data,
          };
        }
      })
      .addCase(updateApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete app
      .addCase(deleteApp.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteApp.fulfilled, (state, action) => {
        state.loading = false;
        state.apps = state.apps.filter((app) => app.id !== action.payload);
      })
      .addCase(deleteApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = appsSlice.actions;
export default appsSlice.reducer;
